import { useEffect, useState } from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { ownerOf } from '../catalog/component/relations';
import {
  FIELD_ROUTES,
  RESPONSIBILITY_FIELDS,
  ResponsibilityField,
  areaAnchorId,
  responsibilityAreas,
} from '../organisation/responsibilityAreas';

/**
 * A landing page for one of the four fields (DX-275).
 *
 * What stood here before was nothing: each field in the navigation offered
 * Catalog / Docs / APIs links filtered by `system=<field>`, and no such systems
 * exist in the catalog — so every one of those links arrived at an empty list,
 * or at a list ignoring the filter entirely. A reader could not tell whether
 * the field was empty or the portal was broken.
 *
 * This page answers "what is this field, and what is in it" from the two
 * sources that actually know. The areas and their owners come from the
 * responsibility map, which is maintained by hand and is the only place the
 * four fields are written down at all. The services come from the catalog,
 * matched on ownership — the relation the catalog really has, rather than the
 * grouping it does not.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  intro: {
    color: 'var(--ui-text-muted)',
    marginBottom: theme.spacing(3),
    lineHeight: 1.7,
    maxWidth: '46rem',
  },
  areaWrap: {
    height: '100%',
  },
  areaCard: {
    height: '100%',
    borderLeft: `3px solid var(--field-color)`,
  },
  areaOwner: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    marginBottom: theme.spacing(1),
  },
  areaDesc: {
    fontSize: '0.9rem',
    lineHeight: 1.55,
  },
  scope: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.82rem',
    color: 'var(--ui-text-muted)',
    lineHeight: 1.5,
  },
  serviceRow: {
    display: 'block',
    padding: theme.spacing(1, 0),
    borderTop: `1px solid var(--ui-border)`,
    textDecoration: 'none',
  },
  serviceName: {
    fontWeight: 600,
    fontSize: '0.92rem',
  },
  serviceDesc: {
    fontSize: '0.82rem',
    color: 'var(--ui-text-muted)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  empty: {
    color: 'var(--ui-text-muted)',
    fontSize: '0.9rem',
    lineHeight: 1.6,
  },
}));

/**
 * The teams whose services a field's page lists, by the team's slug.
 *
 * The responsibility map records a person per area, the catalog records the
 * team that owns a service (one of the eight of ADR-0104), and nothing joins
 * the two. So each field names the teams its areas fall to: Product
 * Engineering's areas are the themes and Cockpit (Frontend Engineering), the
 * Integration Studio, the AI Studio and the services. Consulting is Customer
 * Enablement; its App Studio area is Product Engineering's apps, listed there.
 * Unmatched services are not lost: they stay in the full catalog, which the
 * page links to. Rendering the areas from the Group entities instead is DX-552.
 */
const OWNER_GROUPS: Record<ResponsibilityField, string[]> = {
  'Platform Engineering': ['platform-engineering'],
  'Product Engineering': [
    'product-engineering',
    'frontend-engineering',
    'integration-engineering',
    'ai-engineering',
  ],
  'Developer Experience': ['developer-experience'],
  Consulting: ['customer-enablement'],
};

/**
 * Whether one of the field's teams owns the entity, read from its owner as the
 * catalog resolved it — so a bare `platform-engineering`, a
 * `group:platform-engineering` and a full ref count alike, and a User or a
 * group in another namespace that shares a team's name does not.
 */
const ownerMatches = (entity: Entity, field: ResponsibilityField): boolean => {
  const owner = ownerOf(entity)?.toLocaleLowerCase('en-US');
  return OWNER_GROUPS[field].some(slug => owner === `group:default/${slug}`);
};

export const AreaPage = ({ field }: { field: ResponsibilityField }) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [services, setServices] = useState<Entity[] | undefined>();
  const [error, setError] = useState<Error | undefined>();

  const areas = responsibilityAreas.filter(a => a.field === field);
  const { color } = RESPONSIBILITY_FIELDS[field];
  const { label } = FIELD_ROUTES[field];

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(res => {
        if (!cancelled) {
          setServices(res.items.filter(e => ownerMatches(e, field)));
        }
      })
      .catch(e => {
        if (!cancelled) {
          setError(e as Error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, field]);

  return (
    <Page themeId="home">
      <Header title={label} subtitle={field} />
      <Content>
        <Typography className={classes.intro}>
          {areas.length} {areas.length === 1 ? 'Bereich' : 'Bereiche'} in{' '}
          {field}. Wer was verantwortet, steht in der{' '}
          <Link to="/verantwortung">Verantwortungslandkarte</Link>; die
          vollständige Liste aller Services steht im{' '}
          <Link to="/catalog">Katalog</Link>.
        </Typography>

        <Grid container spacing={3}>
          {areas.map(area => (
            <Grid item xs={12} md={6} key={area.bereich}>
              {/* The field's colour rides in on a wrapper: InfoCard takes no
                  style prop, and the card's left rule reads the variable. */}
              <div
                className={classes.areaWrap}
                style={{ ['--field-color' as any]: color }}
              >
                <InfoCard title={area.bereich} className={classes.areaCard}>
                  <Typography className={classes.areaOwner}>
                    {area.owner}
                  </Typography>
                  <Typography className={classes.areaDesc}>
                    {area.mission}
                  </Typography>
                  <Typography className={classes.scope}>
                    <strong>Dazu gehört:</strong> {area.scopeIn}
                  </Typography>
                  <Typography className={classes.scope}>
                    <strong>Nicht dazu:</strong> {area.scopeOut}
                  </Typography>
                  <Typography className={classes.scope}>
                    <Link to={`/verantwortung#${areaAnchorId(area.owner)}`}>
                      Details in der Verantwortungslandkarte
                    </Link>
                  </Typography>
                </InfoCard>
              </div>
            </Grid>
          ))}

          <Grid item xs={12}>
            <InfoCard title="Services in diesem Feld">
              {error && <ResponseErrorPanel error={error} />}
              {!error && services === undefined && <Progress />}
              {!error && services?.length === 0 && (
                <Typography className={classes.empty}>
                  Für dieses Feld ist im Katalog noch kein Service auf ein Team
                  dieses Feldes eingetragen. Das heißt nicht, dass es keine gibt
                  — nur, dass die Zuordnung im Katalog fehlt. Der{' '}
                  <Link to="/catalog">vollständige Katalog</Link> zeigt alle
                  Services.
                </Typography>
              )}
              {!error &&
                services?.map(entity => (
                  <Link
                    key={entity.metadata.name}
                    to={`/catalog/${
                      entity.metadata.namespace ?? 'default'
                    }/component/${entity.metadata.name}`}
                    className={classes.serviceRow}
                  >
                    <Typography className={classes.serviceName}>
                      {entity.metadata.title ?? entity.metadata.name}
                    </Typography>
                    {entity.metadata.description && (
                      <Typography className={classes.serviceDesc}>
                        {entity.metadata.description}
                      </Typography>
                    )}
                  </Link>
                ))}
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
